<script setup>
import { computed } from "vue";
import { storeToRefs } from "pinia";
import {
  StealieText,
  StealieButton,
  StealieTabs,
  StealieTabPanel,
} from "@get-overalls/fe-stealie";
import { SparklesIcon } from "@heroicons/vue/24/outline";
import { useRequestsStore } from "@/stores/requests";
import RequestRow from "@/components/RequestRow.vue";
import SkeletonRequestsList from "@/components/skeletons/SkeletonRequestsList.vue";

const emit = defineEmits(["create-request"]);

const requestsStore = useRequestsStore();
const { inProgressRequests, openRequests, inProgressAndOpenRequests, closedRequests } =
  storeToRefs(requestsStore);

const tabNames = computed(() => {
  const tabs = [
    `To do ${inProgressAndOpenRequests.value.length > 0 ? `(${inProgressAndOpenRequests.value.length})` : ""}`,
  ];
  if (closedRequests.value.length > 0) {
    tabs.push(`Completed (${closedRequests.value.length})`);
  }
  return tabs;
});
</script>

<template>
  <SkeletonRequestsList
    v-if="!requestsStore.inProgressLoaded || !requestsStore.openLoaded"
  />
  <StealieTabs v-else :tabs="tabNames">
    <StealieTabPanel :value="tabNames[0]">
      <div
        v-if="inProgressAndOpenRequests.length === 0"
        class="grid min-h-[50vh] place-content-center"
      >
        <div class="flex max-w-64 flex-col items-center">
          <SparklesIcon class="mb-2 size-36 stroke-[0.5] text-brand-gray-200" />
          <StealieText as="h2" size="lg" bold class="mb-4 font-bold text-brand-gray-500">
            No open requests
          </StealieText>
          <StealieParagraph size="md" class="text-center text-brand-gray-500">
            Use the
            <StealieButton variant="tertiary" @click="emit('create-request')">
              Create a request
            </StealieButton>
            button above to start a new request
          </StealieParagraph>
        </div>
      </div>
      <div v-else>
        <section v-if="inProgressRequests.length > 0" class="py-6">
          <StealieText as="h2" size="lg" bold class="mb-6 text-brand-gray-600">
            {{ inProgressRequests.length }} request{{
              inProgressRequests.length > 1 ? "s" : ""
            }}
            in progress
          </StealieText>
          <RequestRow
            v-for="request in inProgressRequests"
            :key="request.id"
            :request="request"
            :category-image-url="request.survey_category_image_url"
            :category-name="request.survey_category_name"
          />
        </section>

        <section
          v-if="openRequests.length > 0"
          :class="[
            'pt-6',
            {
              'border-t border-dashed border-brand-primary-teal-400':
                inProgressRequests.length > 0 && openRequests.length > 0,
            },
          ]"
        >
          <StealieText as="h2" size="lg" bold class="mb-6 text-brand-gray-600">
            {{ openRequests.length }} request{{
              openRequests.length > 1 ? "s are" : " is"
            }}
            in queue
          </StealieText>
          <div class="flex flex-col gap-y-2">
            <RequestRow
              v-for="request in openRequests"
              :key="request.id"
              :request="request"
              :category-image-url="request.survey_category_image_url"
              :category-name="request.survey_category_name"
              compact
            />
          </div>
        </section>
      </div>
    </StealieTabPanel>
    <StealieTabPanel v-if="tabNames[1]" :value="tabNames[1]">
      <section class="my-6">
        <StealieText as="h2" size="lg" bold class="mb-6 text-brand-gray-600">
          {{ closedRequests.length }} request{{ closedRequests.length > 1 ? "s" : "" }}
          completed
        </StealieText>
        <div class="flex flex-col gap-y-2">
          <RequestRow
            v-for="request in closedRequests"
            :key="request.id"
            :request="request"
            :category-image-url="request.survey_category_image_url"
            :category-name="request.survey_category_name"
            compact
          />
        </div>
      </section>
    </StealieTabPanel>
  </StealieTabs>
</template>
